import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { formatDateTime, formatHrs1 } from './historyFormatters';
import {
  tableSx,
  headSx,
  groupHeadSx,
  numSx,
  valueSx,
  dividerLeftSx,
  subHeadStickySx,
  tooltipPopperProps,
} from './historyTableStyles';

const tableLayoutSx = {
  ...tableSx,
  width: '100%',
  tableLayout: 'fixed',
};

// Single-line cell: never wrap; overflow is clipped with an ellipsis.
const ellipsisSx = {
  ...valueSx,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// source column value -> short chip label
const SOURCE_LABELS = {
  manual_edit: 'Manual',
  csv_upload: 'CSV',
  script: 'Script',
};

/** Small chip for how the change was made (Manual / CSV / Script). */
function SourceChip({ source }) {
  if (!source) return <span>—</span>;
  return (
    <Chip
      label={SOURCE_LABELS[source] || source}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 500,
        bgcolor: 'var(--phm-surface-strong)',
        color: 'var(--phm-text-header)',
        border: '1px solid var(--phm-border)',
      }}
    />
  );
}

/** Hours cell; optional change highlight and column-group divider. */
function HrsCell({ hrs, changed = false, divider = false }) {
  return (
    <TableCell
      sx={{
        ...numSx,
        ...valueSx,
        ...(divider ? dividerLeftSx : {}),
        ...(changed
          ? {
              bgcolor: 'var(--phm-accent-bg)',
              color: 'var(--phm-accent-text)',
              fontWeight: 600,
            }
          : {}),
      }}
    >
      {formatHrs1(hrs)}
    </TableCell>
  );
}

/** Single-line truncated text; hover shows the full value. */
function TruncatedTextCell({ text }) {
  if (!text) return <TableCell sx={valueSx}>—</TableCell>;
  return (
    <TableCell sx={ellipsisSx}>
      <Tooltip title={text} PopperProps={tooltipPopperProps}>
        <Box
          component="span"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

/**
 * BudgetChangeTable — budgeted-hours changes (ticket #160), oldest first.
 * Two-row header grouping Field/Shop/Total "Before" vs "After", mirroring
 * BudgetSplitTable. "After" cells whose value changed are highlighted; null
 * "before" values (package had no budget yet) show as em-dashes.
 */
function BudgetChangeTable({ rows = [] }) {
  if (!rows.length) {
    return <div className="phm-empty">No records found.</div>;
  }

  return (
    <Table size="small" stickyHeader sx={tableLayoutSx}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...headSx, width: 200 }} rowSpan={2}>
            Date &amp; Time
          </TableCell>
          <TableCell sx={{ ...headSx, width: '16%' }} rowSpan={2}>
            Reason
          </TableCell>
          <TableCell sx={{ ...headSx, width: '8%' }} rowSpan={2}>
            CO Ref
          </TableCell>
          <TableCell sx={{ ...groupHeadSx, width: '16%' }} colSpan={3}>
            Before
          </TableCell>
          <TableCell
            sx={{ ...groupHeadSx, ...dividerLeftSx, width: '16%' }}
            colSpan={3}
          >
            After
          </TableCell>
          <TableCell sx={{ ...headSx, width: '10%' }} rowSpan={2}>
            Changed by
          </TableCell>
          <TableCell sx={{ ...headSx, width: '8%' }} rowSpan={2}>
            Source
          </TableCell>
          <TableCell sx={{ ...headSx, width: '10%' }} rowSpan={2}>
            Notes
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Field
          </TableCell>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Shop
          </TableCell>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Total
          </TableCell>
          <TableCell
            sx={{ ...headSx, ...numSx, ...dividerLeftSx, ...subHeadStickySx }}
          >
            Field
          </TableCell>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Shop
          </TableCell>
          <TableCell sx={{ ...headSx, ...numSx, ...subHeadStickySx }}>
            Total
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          // A null "before" value means there was no budget to compare against,
          // so the "after" value is shown plain rather than as a change.
          const changed = (oldV, newV) =>
            oldV != null && Number(newV) !== Number(oldV);
          return (
            <TableRow key={r.id}>
              <TableCell sx={valueSx}>{formatDateTime(r.changedAt)}</TableCell>
              <TruncatedTextCell text={r.changeReason} />
              <TableCell sx={ellipsisSx}>
                {r.changeOrderReference || '—'}
              </TableCell>
              <HrsCell hrs={r.oldFieldHrs} />
              <HrsCell hrs={r.oldShopHrs} />
              <HrsCell hrs={r.oldTotalHrs} />
              <HrsCell
                hrs={r.newFieldHrs}
                changed={changed(r.oldFieldHrs, r.newFieldHrs)}
                divider
              />
              <HrsCell
                hrs={r.newShopHrs}
                changed={changed(r.oldShopHrs, r.newShopHrs)}
              />
              <HrsCell
                hrs={r.newTotalHrs}
                changed={changed(r.oldTotalHrs, r.newTotalHrs)}
              />
              <TableCell sx={ellipsisSx}>{r.changedByName || '—'}</TableCell>
              <TableCell>
                <SourceChip source={r.source} />
              </TableCell>
              <TruncatedTextCell text={r.notes} />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default BudgetChangeTable;
